import { useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

/**
 * NoteTextEditor Component
 * 
 * A component that renders either a read-only view of note content
 * or an editable textarea for note creation/editing
 * 
 * @param {string} content - The note content
 * @param {boolean} readOnly - Whether the note is in read-only mode
 * @param {Function} onChange - Handler for content changes
 * @param {Function} onCursorPositionChange - Handler for cursor position changes
 * @param {React.RefObject} textAreaRef - Reference to the textarea for DOM manipulation
 * @returns {JSX.Element} - The rendered component
 */
const NoteTextEditor = ({
  content,
  readOnly,
  onChange,
  onCursorPositionChange,
  textAreaRef
}) => {
  // Handle text and cursor position changes
  const handleTextChange = (e) => {
    onChange(e);
    
    // Update cursor position if handler provided
    if (onCursorPositionChange) {
      onCursorPositionChange(e.target.selectionStart);
    }
  };

  // Focus the textarea when mounted if not in readOnly mode
  useEffect(() => {
    if (!readOnly && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [readOnly, textAreaRef]);

  return (
    <Paper 
      elevation={0}
      className='note-input-card'
      sx={{ 
        width: '100%', 
        overflow: 'auto',
        backgroundColor: 'transparent'
      }}
    >
      {readOnly ? (
        <Box 
          marginBottom={2}
          padding={2}
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.5
          }}
        >
          <Typography
            variant="body1"
            component="div"
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            {content || 'No content'}
          </Typography>
        </Box>
      ) : (
        <textarea
          ref={textAreaRef}
          className='note-textarea'
          value={content}
          onChange={handleTextChange}
          placeholder="Type your note here... Use @ to mention entities"
          disabled={readOnly}
          tabIndex={readOnly ? -1 : 0}
        />
      )}
    </Paper>
  );
};

NoteTextEditor.propTypes = {
  content: PropTypes.string,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  onCursorPositionChange: PropTypes.func,
  textAreaRef: PropTypes.oneOfType([
    PropTypes.func, 
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]).isRequired
};

NoteTextEditor.defaultProps = {
  content: '',
  readOnly: false,
  onChange: () => {},
  onCursorPositionChange: null
};

export default NoteTextEditor;
